/** Billing query + mutation hooks. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  fetchBill,
  fetchBillingKpis,
  fetchHouseBills,
  fetchPeriodBills,
  fetchPeriods,
  fetchPublicBill,
  generatePeriod,
  issueBill,
  issueDraftBills,
  payBill,
  voidBill,
} from "@/features/billing/api";

export const usePeriods = () =>
  useQuery({ queryKey: ["billing", "periods"], queryFn: ({ signal }) => fetchPeriods(signal) });

export const useBillingKpis = () =>
  useQuery({
    queryKey: ["billing", "kpis"],
    queryFn: ({ signal }) => fetchBillingKpis(signal),
  });

export const usePeriodBills = (id: number, locality?: string | null) =>
  useQuery({
    queryKey: ["billing", "period", id, "bills", locality ?? null],
    queryFn: ({ signal }) => fetchPeriodBills(id, locality, signal),
    enabled: id > 0,
  });

/** A house's real bill history — admin/manager-only (mirrors the backend's
 * `require_role("admin", "manager")` guard on `/billing/house/{id}/bills`).
 * `enabled` lets the caller skip the request entirely for a role that would
 * only get a 403, rather than firing it and swallowing the error. */
export const useHouseBills = (houseId: string | null, enabled: boolean) =>
  useQuery({
    queryKey: ["billing", "house", houseId, "bills"],
    queryFn: ({ signal }) => fetchHouseBills(houseId as string, signal),
    enabled: Boolean(houseId) && enabled,
  });

export function useIssueDraftBills(periodId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => issueDraftBills(periodId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["billing"] }),
  });
}

export const useBill = (id: number) =>
  useQuery({
    queryKey: ["billing", "bill", id],
    queryFn: ({ signal }) => fetchBill(id, signal),
    enabled: id > 0,
  });

export function useGeneratePeriod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: generatePeriod,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["billing", "periods"] }),
  });
}

const billActions = { issue: issueBill, pay: payBill, void: voidBill } as const;

export function useBillAction(kind: keyof typeof billActions) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => billActions[kind](id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["billing"] }),
  });
}

/** The public, unauthenticated bill view — same data as `useBill`, fetched by
 * the signed share-link token instead of an admin-authenticated id. */
export const usePublicBill = (token: string | undefined) =>
  useQuery({
    queryKey: ["billing", "public-bill", token],
    queryFn: ({ signal }) => fetchPublicBill(token as string, signal),
    enabled: Boolean(token),
    retry: false, // an invalid/expired token won't succeed on retry
  });
